import { lazy, Suspense } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import { ArcadeButton, Text } from '@cash-design-system/react'
import { TagConfirmationBlockerTagConfirmationBlockerStyle } from '@cash-web/protos/squareup/franklin/blockers/tag_confirmation_blocker.pb'
import { type BlockerComponentProps, useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, type BaseBlockerFormFields } from '../../BaseBlocker/BaseBlocker'

const CashAppTagHero = lazy(() =>
  import('@cash-web/shared-card-studio-feature-main').then(module => ({
    default: module.CashAppTagHero,
  }))
)

const HeroContainer = styled.div<{ $dimmed: boolean }>`
  width: 100%;
  min-height: 15rem;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.5 : 1)};
`

const Title = styled(Text)`
  display: flex;
  flex-direction: column;
  margin: 0;
`

const HighlightedTitle = styled.span<{ $isFailure: boolean }>`
  color: ${({ $isFailure, theme }) =>
    $isFailure ? theme.arcadeColors.semantic.textStandard : theme.arcadeColors.semantic.textSubtle};
`

const StandardTitle = styled.span<{ $isFailure: boolean }>`
  color: ${({ $isFailure, theme }) =>
    $isFailure ? theme.arcadeColors.semantic.textSubtle : theme.arcadeColors.semantic.textStandard};
`

export const BlockerTagConfirmation = ({ blocker }: BlockerComponentProps<'tag_confirmation_blocker'>) => {
  const form = useForm<BaseBlockerFormFields>()
  const { isLoading, skipBlocker } = useFlowContext()
  const isFailure =
    blocker.style === TagConfirmationBlockerTagConfirmationBlockerStyle.TAG_CONFIRMATION_BLOCKER_STYLE_FAILURE

  return (
    <BaseBlocker
      buttons={
        blocker.button_title
          ? [
              <ArcadeButton
                disabled={isLoading}
                isLoading={isLoading}
                key="done"
                prominence="prominent"
                size="cta"
                type="button"
                onClick={() => {
                  void skipBlocker()
                }}
              >
                {blocker.button_title}
              </ArcadeButton>,
            ]
          : []
      }
      form={form}
      onSubmit={() => undefined}
    >
      <HeroContainer $dimmed={isFailure}>
        {blocker.tag_theme_token ? (
          <Suspense fallback={null}>
            <CashAppTagHero tagThemeToken={blocker.tag_theme_token} />
          </Suspense>
        ) : null}
      </HeroContainer>
      {blocker.highlighted_title || blocker.standard_title ? (
        <Title as="h2" color="textStandard" typography="headlineSmall">
          {blocker.highlighted_title ? (
            <HighlightedTitle $isFailure={isFailure}>{blocker.highlighted_title}</HighlightedTitle>
          ) : null}
          {blocker.standard_title ? (
            <StandardTitle $isFailure={isFailure}>{blocker.standard_title}</StandardTitle>
          ) : null}
        </Title>
      ) : null}
    </BaseBlocker>
  )
}
